import { useMemo, useState } from 'react';
import { useLookups, useRows } from '@/hooks/useLive';
import { useView } from '@/hooks/useView';
import { ExportBar } from '@/components/ui/ExportBar';
import { figures, tableRows } from '@/lib/export';
import { date as fmtDate, hours as fmtHours, humanise, num } from '@/lib/format';
import { useAuthStore } from '@/stores/authStore';
import { roleLock } from '@/lib/editability';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { StatTile } from '@/components/ui/StatTile';
import { StatusChip } from '@/components/ui/StatusChip';
import { asRag, ragTone } from '@/components/ui/statusTone';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { ServiceDrawer } from './maintenance/ServiceDrawer';
import { LinerDrawer } from './maintenance/LinerDrawer';

export interface ServiceStatus {
  machine_id: string;
  fleet_no: string;
  current_hour_meter: number;
  service_interval_hours: number | null;
  last_service_hours: number | null;
  last_service_date: string | null;
  next_service_hours: number | null;
  hours_remaining: number | null;
  status_colour: string;
  /** Added in 0034. Absent before the migration is applied: treat as hours. */
  meter_unit?: 'hours' | 'km';
}

export interface LinerLife {
  id: string;
  fleet_no: string;
  component_name: string | null;
  part_no: string | null;
  description: string;
  installed_on: string;
  installed_hour_meter: number;
  removed_on: string | null;
  removed_hour_meter: number | null;
  life_hours: number | null;
  expected_life_hours: number | null;
  quantity: number;
  total_cost: number | null;
  reason_for_replacement: string | null;
  condition_on_removal: string | null;
  is_current: boolean;
  hours_in_service: number | null;
  hours_to_expected_change: number | null;
}

interface LinerAverage {
  fleet_no: string;
  component_name: string | null;
  part_no: string | null;
  changes: number;
  avg_life_hours: number | null;
  min_life_hours: number | null;
  max_life_hours: number | null;
  avg_cost: number | null;
}

interface ServiceRecord extends Record<string, unknown> {
  id: string;
  machine_id: string;
  service_type: string;
  scheduled_hours: number | null;
  scheduled_date: string | null;
  performed_at: string | null;
  performed_hours: number | null;
  next_service_hours: number | null;
  status: string;
  technician: string | null;
  workshop: string | null;
  downtime_hours: number | null;
  cost: number | null;
  work_done: string | null;
}

interface LinerRow extends Record<string, unknown> {
  id: string;
  machine_id: string;
  component_id: string | null;
  is_current: boolean;
}

const SERVICE_TONE: Record<string, 'grey' | 'blue' | 'amber' | 'green' | 'red'> = {
  planned: 'blue',
  due: 'amber',
  overdue: 'red',
  completed: 'green',
  cancelled: 'grey',
};

/** Row colour follows the RAG, so amber reads as a warning rather than a
 *  failure — a service 20 hours away is not the same thing as an overdue one. */
function rowClass(rag: string): string {
  if (rag === 'red') return 'issue issue--block';
  if (rag === 'amber') return 'issue issue--warn';
  return 'issue issue--ok';
}

/** A liner is amber inside the last fifth of its expected life, red past it. */
function linerRag(l: LinerLife): 'green' | 'amber' | 'red' | 'grey' {
  if (!l.expected_life_hours || l.hours_to_expected_change == null) return 'grey';
  if (l.hours_to_expected_change <= 0) return 'red';
  if (l.hours_to_expected_change <= l.expected_life_hours * 0.2) return 'amber';
  return 'green';
}

export function Maintenance() {
  const [tab, setTab] = useState<'services' | 'liners'>('services');
  const [service, setService] = useState<Partial<ServiceRecord> | null>(null);
  const [liner, setLiner] = useState<Partial<LinerRow> | null>(null);

  const services = useRows<ServiceRecord>('service_records', 'scheduled_date');
  const liners = useRows<LinerRow>('liner_installations', 'installed_on');
  const lookups = useLookups([
    { table: 'machines', label: 'fleet_no' },
    { table: 'machine_components', label: 'name' },
    { table: 'suppliers', label: 'name' },
  ]);

  const { rows: board } = useView<ServiceStatus>('v_service_status');
  const { rows: linerLife } = useView<LinerLife>('v_liner_life');
  const { rows: linerAvg } = useView<LinerAverage>('v_liner_life_average');

  const canEdit = useAuthStore((s) => s.hasRole('maintenance', 'manager', 'admin'));
  const role = useAuthStore((s) => s.profile?.role);
  // Everyone may open a record; who may change it is said inside (0046).
  const lock = roleLock(role, ['maintenance', 'manager', 'admin'], 'services and liners');

  const ranked = useMemo(
    () => [...(board ?? [])].sort((a, b) => (a.hours_remaining ?? 1e9) - (b.hours_remaining ?? 1e9)),
    [board],
  );

  const monthStart = new Date().toISOString().slice(0, 8) + '01';

  const summary = useMemo(() => {
    const list = services ?? [];
    const done = list.filter(
      (s) => s.status === 'completed' && (s.performed_at ?? '') >= monthStart,
    );
    return {
      overdue: ranked.filter((m) => (m.hours_remaining ?? 1) <= 0).length,
      dueSoon: ranked.filter((m) => (m.hours_remaining ?? 1e9) > 0 && m.status_colour === 'amber').length,
      completed: done.length,
      downtime: done.reduce((s, r) => s + (Number(r.downtime_hours) || 0), 0),
      cost: done.reduce((s, r) => s + (Number(r.cost) || 0), 0),
      linersDue: (linerLife ?? []).filter((l) => l.is_current && linerRag(l) !== 'green' && linerRag(l) !== 'grey').length,
    };
  }, [services, ranked, linerLife, monthStart]);

  const current = useMemo(
    () => (linerLife ?? [])
      .filter((l) => l.is_current)
      .sort((a, b) => (a.hours_to_expected_change ?? 1e9) - (b.hours_to_expected_change ?? 1e9)),
    [linerLife],
  );

  const history = useMemo(
    () => (linerLife ?? []).filter((l) => !l.is_current),
    [linerLife],
  );

  const serviceColumns: Column<ServiceRecord>[] = [
    {
      key: 'machine_id', label: 'Machine',
      value: (s) => lookups.machines?.get(s.machine_id) ?? '',
      render: (s) => <strong>{lookups.machines?.get(s.machine_id) ?? '—'}</strong>,
    },
    { key: 'service_type', label: 'Service' },
    {
      key: 'scheduled_hours', label: 'Due at', align: 'right',
      render: (s) => <span className="tabular">{fmtHours(s.scheduled_hours)}</span>,
    },
    { key: 'scheduled_date', label: 'Planned', render: (s) => fmtDate(s.scheduled_date) },
    { key: 'performed_at', label: 'Done', render: (s) => fmtDate(s.performed_at) },
    {
      key: 'performed_hours', label: 'At hours', align: 'right',
      render: (s) => <span className="tabular">{fmtHours(s.performed_hours)}</span>,
    },
    {
      key: 'downtime_hours', label: 'Downtime', align: 'right',
      render: (s) => <span className="tabular">{fmtHours(s.downtime_hours)}</span>,
    },
    { key: 'technician', label: 'Technician' },
    {
      key: 'status', label: 'Status',
      render: (s) => <StatusChip tone={SERVICE_TONE[s.status] ?? 'grey'}>{humanise(s.status)}</StatusChip>,
    },
  ];

  const linerColumns: Column<LinerLife>[] = [
    { key: 'fleet_no', label: 'Machine', render: (l) => <strong>{l.fleet_no}</strong> },
    { key: 'component_name', label: 'Component', render: (l) => l.component_name ?? '—' },
    { key: 'description', label: 'Liner' },
    { key: 'installed_on', label: 'Installed', render: (l) => fmtDate(l.installed_on) },
    { key: 'removed_on', label: 'Removed', render: (l) => fmtDate(l.removed_on) },
    {
      key: 'life_hours', label: 'Life achieved', align: 'right',
      render: (l) => <span className="tabular">{fmtHours(l.life_hours)} h</span>,
    },
    {
      key: 'expected_life_hours', label: 'Expected', align: 'right',
      render: (l) => <span className="tabular">{fmtHours(l.expected_life_hours)}</span>,
    },
    {
      key: 'variance', label: 'vs expected', align: 'right',
      value: (l) => (l.life_hours != null && l.expected_life_hours
        ? l.life_hours - l.expected_life_hours : null),
      render: (l) => {
        if (l.life_hours == null || !l.expected_life_hours) return '—';
        const d = l.life_hours - l.expected_life_hours;
        return (
          <StatusChip tone={d >= 0 ? 'green' : 'amber'}>
            {d >= 0 ? '+' : '−'}{fmtHours(Math.abs(d))} h
          </StatusChip>
        );
      },
    },
    {
      key: 'condition_on_removal', label: 'Condition',
      render: (l) => humanise(l.condition_on_removal),
    },
  ];

  const avgColumns: Column<LinerAverage>[] = [
    { key: 'fleet_no', label: 'Machine' },
    { key: 'component_name', label: 'Component', render: (a) => a.component_name ?? '—' },
    { key: 'part_no', label: 'Part' },
    { key: 'changes', label: 'Changes', align: 'right' },
    {
      key: 'avg_life_hours', label: 'Average life', align: 'right',
      render: (a) => <span className="tabular">{fmtHours(a.avg_life_hours)} h</span>,
    },
    {
      key: 'min_life_hours', label: 'Shortest', align: 'right',
      render: (a) => <span className="tabular">{fmtHours(a.min_life_hours)}</span>,
    },
    {
      key: 'max_life_hours', label: 'Longest', align: 'right',
      render: (a) => <span className="tabular">{fmtHours(a.max_life_hours)}</span>,
    },
    {
      key: 'avg_cost', label: 'Average cost', align: 'right',
      render: (a) => <span className="tabular">{num(a.avg_cost, 2)}</span>,
    },
  ];

  return (
    <div className="col" style={{ gap: 'var(--sp-3)' }}>
      <div className="row">
        <ExportBar title="Maintenance" subtitle={tab === 'services' ? 'Services' : 'Liners & wear parts'}
          file={`maintenance-${tab}`} landscape disabled={!board}
          sheets={() => [
            { name: 'Summary', rows: figures([
              ['Services overdue', summary.overdue], ['Due soon', summary.dueSoon],
              ['Liners near end of life', summary.linersDue], ['Services this month', summary.completed],
              ['Service downtime this month', Math.round(summary.downtime * 10) / 10, 'h'],
              ['Service cost this month', Math.round(summary.cost * 100) / 100, 'N$']]) },
            { name: 'Service board', rows: ranked.map((m) => ({
              Machine: m.fleet_no, Meter: Number(m.current_hour_meter), Unit: m.meter_unit ?? 'hours',
              Interval: m.service_interval_hours ?? '', 'Last service at': m.last_service_hours ?? '',
              'Last service': m.last_service_date ?? '', 'Next at': m.next_service_hours ?? '',
              Remaining: m.hours_remaining ?? '', Status: m.status_colour })) },
            { name: 'Service register', rows: tableRows(serviceColumns, services) },
            { name: 'Liners in service', rows: tableRows(linerColumns, current) },
            { name: 'Liner history', rows: tableRows(linerColumns, history) },
            { name: 'Average liner life', rows: tableRows(avgColumns, linerAvg) },
          ]} />
      </div>
      <Panel title="Maintenance position">
        <div className="grid grid--stats">
          <StatTile label="Services overdue" value={summary.overdue}
            rag={summary.overdue ? 'red' : 'green'} />
          <StatTile label="Due soon" value={summary.dueSoon}
            rag={summary.dueSoon ? 'amber' : 'green'} />
          <StatTile label="Liners near end of life" value={summary.linersDue}
            rag={summary.linersDue ? 'amber' : 'green'} />
          <StatTile label="Services this month" value={summary.completed} />
          <StatTile label="Service downtime" value={summary.downtime} decimals={1}
            sub="hours, this month" />
          <StatTile label="Service cost" value={summary.cost} decimals={2}
            sub="this month" />
        </div>
      </Panel>

      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <Button variant={tab === 'services' ? 'primary' : 'secondary'} onClick={() => setTab('services')}>
          Services
        </Button>
        <Button variant={tab === 'liners' ? 'primary' : 'secondary'} onClick={() => setTab('liners')}>
          Liners &amp; wear parts
        </Button>
        <span className="spacer" />
        {canEdit && (
          tab === 'services'
            ? <Button onClick={() => setService({})}>+ Log service</Button>
            : <Button onClick={() => setLiner({})}>+ Install liner</Button>
        )}
      </div>

      {tab === 'services' ? (
        <>
          <Panel title="Service board">
            <div className="col">
              {ranked.length === 0 && (
                <p className="small muted" style={{ marginBottom: 0 }}>
                  No machine has a service interval set. Add one on the machine record
                  and it appears here.
                </p>
              )}
              {ranked.map((m) => {
                const rag = asRag(m.status_colour);
                const overdue = (m.hours_remaining ?? 1) <= 0;
                const km = m.meter_unit === 'km';
                const sfx = km ? 'km' : 'h';
                const fmt = (x: number | null | undefined) => (km ? num(x ?? 0, 0) : fmtHours(x));
                return (
                  <div
                    className={rowClass(rag)}
                    key={m.machine_id}
                    style={{ alignItems: 'center', flexWrap: 'wrap', cursor: canEdit ? 'pointer' : undefined }}
                    onClick={canEdit ? () => setService({
                      machine_id: m.machine_id,
                      scheduled_hours: m.next_service_hours,
                      performed_hours: m.current_hour_meter,
                      service_type: m.service_interval_hours
                        ? `${num(m.service_interval_hours)} ${sfx} service` : 'Routine service',
                    }) : undefined}
                  >
                    <strong style={{ minWidth: 110 }}>{m.fleet_no}</strong>
                    <StatusChip tone={ragTone(m.status_colour)}>
                      {overdue
                        ? `${fmt(Math.abs(m.hours_remaining ?? 0))} ${sfx} overdue`
                        : `${fmt(m.hours_remaining)} ${sfx} remaining`}
                    </StatusChip>
                    <span className="small muted">
                      {km ? 'odometer' : 'meter'} {fmt(m.current_hour_meter)} · due at {fmt(m.next_service_hours)} {sfx}
                    </span>
                    <span className="spacer" />
                    <span className="small muted">
                      {m.last_service_date
                        ? `last ${fmtDate(m.last_service_date)}`
                        : 'no service recorded yet'}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="small muted" style={{ marginBottom: 0 }}>
              Hours (or kilometres) remaining come from the live meter, so a service falls due
              as the machine works — not on a date somebody has to remember. Marking
              a service completed rolls the next one forward automatically.
            </p>
          </Panel>

          <Panel title="Service register" flush>
            <DataTable
              rows={services}
              columns={serviceColumns}
              rowKey={(s) => s.id}
              onRowClick={(s) => setService(s)}
              defaultSort="scheduled_date"
              searchPlaceholder="Search machine, technician, service…"
              empty="No services recorded."
            />
          </Panel>
        </>
      ) : (
        <>
          <Panel title={`In service · ${current.length}`}>
            <div className="col">
              {current.length === 0 && (
                <p className="small muted" style={{ marginBottom: 0 }}>
                  No liners currently installed.
                </p>
              )}
              {current.map((l) => {
                const rag = linerRag(l);
                return (
                  <div
                    className={rowClass(rag)}
                    key={l.id}
                    style={{ alignItems: 'center', flexWrap: 'wrap', cursor: 'pointer' }}
                    onClick={() => setLiner((liners ?? []).find((r) => r.id === l.id) ?? { id: l.id })}
                  >
                    <strong style={{ minWidth: 110 }}>{l.fleet_no}</strong>
                    <span>{l.component_name ?? l.description}</span>
                    <span className="small muted">{fmtHours(l.hours_in_service)} h in service</span>
                    <span className="spacer" />
                    {l.expected_life_hours ? (
                      <StatusChip tone={ragTone(rag)}>
                        {(l.hours_to_expected_change ?? 0) <= 0
                          ? `${fmtHours(Math.abs(l.hours_to_expected_change ?? 0))} h past expected life`
                          : `${fmtHours(l.hours_to_expected_change)} h to change`}
                      </StatusChip>
                    ) : (
                      <span className="small muted">no expected life set</span>
                    )}
                  </div>
                );
              })}
            </div>
          </Panel>

          <Panel title="Average life achieved" flush>
            <DataTable
              rows={linerAvg}
              columns={avgColumns}
              rowKey={(a) => `${a.fleet_no}|${a.component_name ?? ''}|${a.part_no ?? ''}`}
              defaultSort="avg_life_hours"
              searchPlaceholder="Search machine, component, part…"
              empty="No liner has been replaced yet — averages appear after the first change."
            />
          </Panel>

          <Panel title="Liner history" flush>
            <DataTable
              rows={history}
              columns={linerColumns}
              rowKey={(l) => l.id}
              onRowClick={(l) => setLiner((liners ?? []).find((r) => r.id === l.id) ?? { id: l.id })}
              defaultSort="removed_on"
              searchPlaceholder="Search machine, component, liner…"
              empty="No liners removed yet."
            />
          </Panel>
        </>
      )}

      {service && (
        <ServiceDrawer
          record={service}
          board={board ?? []}
          locked={lock}
          onClose={() => setService(null)}
        />
      )}
      {liner && <LinerDrawer record={liner} locked={lock} onClose={() => setLiner(null)} />}
    </div>
  );
}
